import React, { useState, useEffect } from 'react';
import { Modal, Box, TextField, Button, Grid, Switch, FormControlLabel, Typography } from '@mui/material';
import { toast } from 'react-toastify';

const ExternalContactModal = ({ isOpen, onClose, contact, onSubmit, agencyId }) => {
  const [formData, setFormData] = useState({
    description: contact?.description || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    isActive: contact?.isActive || false,
    canPost: contact?.canPost ?? true,
    canRequest: contact?.canRequest ?? false,
  });

  // Reset form data when the contact prop changes (e.g., when editing a new contact)
  useEffect(() => {
    if (contact) {
      setFormData({
        description: contact.description || '',
        email: contact.email || '',
        phone: contact.phone || '',
        isActive: contact.isActive || false,
        canPost: contact.canPost ?? true,
        canRequest: contact.canRequest ?? false,
      });
    } else {
      setFormData({
        description: '',
        email: '',
        phone: '',
        isActive: false,
        canPost: true,
        canRequest: false,
      });
    }
  }, [contact]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      isActive: e.target.checked,
    }));
  };

  const handleCanPostChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      canPost: e.target.checked,
    }));
  };

  const handleCanRequestChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      canRequest: e.target.checked,
    }));
  };

  // Helper function to validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to validate phone number format
  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^\d{10}$/; // Ensures phone number has exactly 10 digits
    return phoneRegex.test(phone);
  };

  const handleSubmit = async () => {
    // Ensure at least one contact field (email or phone) is provided
    if (!formData.email && !formData.phone) {
      toast.error('Please provide either an email or phone number.');
      return;
    }

    // Validate email format if email is provided
    if (formData.email && !isValidEmail(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    // Validate phone number format if phone is provided
    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      toast.error('Please enter a valid 10-digit phone number.');
      return;
    }

    try {
      // Prepare the contact object
      const contactPayload = {
        agentContactId: contact?.agentContactId || 0, // agentContactId will determine if it's an add or update
        agencyId,
        ...formData,
      };

      // Call the onSubmit function passed from the parent
      await onSubmit(contactPayload);
      toast.success(contact ? 'Contact updated successfully!' : 'Contact added successfully!');
      onClose(); // Close the modal after submission
    } catch (error) {
      toast.error('Failed to submit the contact. Please try again.');
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {contact ? 'Edit Contact' : 'Add Contact'}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Name"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Phone (only digits)"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.isActive} onChange={handleSwitchChange} />}
              label="Active"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.canPost} onChange={handleCanPostChange} />}
              label="Can Post (Access Posting Features)"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.canRequest} onChange={handleCanRequestChange} />}
              label="Can Request (Access Requesting Features)"
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" fullWidth onClick={handleSubmit}>
              {contact ? 'Update Contact' : 'Add Contact'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default ExternalContactModal;
